import React from 'react'
import { connect } from 'react-redux'
import { BiCheckCircle, BiPlusCircle } from 'react-icons/bi'
import { selectService } from '../../store/services/actions'
import { ApplicationState } from '../../store'
import { Service, ServiceCategory } from '../../store/services/types'
import { ItemContainer } from '../layout/ItemContainer'
import styled from '../../utils/styled'
import { ContinueButton, ContinueButtonBadge, ContinueButtonText } from '../../styles/components/buttons'
import { Typography } from '../layout/Typography'
import { Box } from '../layout/Box'
import { RouteType } from '../../types/routes'

interface PropsFromState {
  data: ServiceCategory[]
  allSelectedServices: Service[]
}

interface PropsFromDispatch {
  selectService: typeof selectService
}

type AllProps = PropsFromState & PropsFromDispatch

const Services: React.FC<AllProps> = ({ data, allSelectedServices, selectService: dispatchSelectService }) => {
  const isServiceSelected = (selectedService: Service) => allSelectedServices.includes(selectedService)

  return (
    <Box padding={allSelectedServices.length > 0 ? '0 0 1rem' : '0'}>
      <Typography variant="h4" margin="2rem">
        Leistungen auswählen
      </Typography>
      {data.map(serviceCategory => (
        <Box margin="1rem 0 2rem" key={serviceCategory.guid}>
          <Box margin="0 0 1rem">
            <Typography variant="bold" margin="1rem">
              {serviceCategory.name}
            </Typography>
          </Box>

          {serviceCategory.services.map(service => (
            <ItemContainer
              onClick={() => dispatchSelectService(service)}
              rightContent={
                <SelectServiceButton isServiceSelected={isServiceSelected(service)}>
                  {isServiceSelected(service) ? (
                    <IconContainer>
                      <CheckIcon size={32} />
                    </IconContainer>
                  ) : (
                    <IconContainer>
                      <PlusIcon size={32} />
                    </IconContainer>
                  )}
                </SelectServiceButton>
              }
              key={service.guid}
            >
              <Box margin="0 1rem 0 0">
                <Typography variant="bold" margin="0">
                  {service.name}{' '}
                </Typography>
                <Typography variant="body2" margin="0" inline>
                  {service.minuten} min.
                </Typography>
                {service.beschreibung && (
                  <Typography variant="body2" margin="0">
                    {service.beschreibung}
                  </Typography>
                )}
              </Box>
              <Box flex flexDirection="row" alignItems="center">
                {service.rabattPreis !== 0 && <ReducedPrice>{service.rabattPreis} €</ReducedPrice>}
                <ServicePrice>{service.preis} €</ServicePrice>
              </Box>
            </ItemContainer>
          ))}
        </Box>
      ))}

      {allSelectedServices.length > 0 && (
        <ContinueButton to={RouteType.AvailableTimes}>
          <ContinueButtonText>Fortfahren</ContinueButtonText>

          <ContinueButtonBadge>{allSelectedServices.length}</ContinueButtonBadge>
        </ContinueButton>
      )}
    </Box>
  )
}

const CheckIcon = styled(BiCheckCircle)`
  color: ${props => props.theme.colors.black};
`

const PlusIcon = styled(BiPlusCircle)`
  color: ${props => props.theme.colors.borders};
`

const IconContainer = styled.div`
  display: flex;
`

const ServicePrice = styled.p`
  color: ${props => props.theme.colors.price};
  font-size: ${props => props.theme.fontSizes.body1};
  margin: 0.5rem 0 0;
`

const ReducedPrice = styled.p`
  color: ${props => props.theme.colors.canceled};
  text-decoration: line-through;
  font-size: ${props => props.theme.fontSizes.body2};
  margin: 0.5rem 0.5rem 0 0;
`

const SelectServiceButton = styled.button<{ isServiceSelected: boolean }>`
  background-color: transparent;
  font-size: 2rem;
  font-weight: 100;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
`

const mapStateToProps = ({ services }: ApplicationState) => ({
  data: services.data,
  allSelectedServices: services.selected
})

const mapDispatchToProps = {
  selectService
}

export default connect(mapStateToProps, mapDispatchToProps)(Services)
